import {
  createColumnHelper,
  flexRender,
  type PaginationState,
  RowSelectionState,
  type SortingState,
  useTable
} from '@tanstack/react-table'
import {
  ContractAnalysisRow,
  Mtds,
  selectablePaginatedTableFeatures,
  ReviewCombinationModal,
  SaveChangesBanner,
  SubstanceTags
} from 'prove-common'
import {
  Dispatch,
  type ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Link } from 'react-router'

type CombinationsTableProps = {
  data: ContractAnalysisRow[]
  showCodes: boolean
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const renderWithCode = (
  name?: string,
  code?: string,
  showCodes = true
): ReactNode => {
  if (name == null || name.length === 0) return '-'
  if (!showCodes || code == null || code.length === 0) return name

  return (
    <span>
      {name} <Mtds.Muted>({code})</Mtds.Muted>
    </span>
  )
}

export const StatusCell = ({
  status
}: {
  status: ContractAnalysisRow['status']
}) => {
  const meta: Record<
    ContractAnalysisRow['status'],
    { color: 'success' | 'danger' | 'warning'; label: string }
  > = {
    approved: { color: 'success', label: 'Approved' },
    declined: { color: 'danger', label: 'Declined' },
    awaiting: { color: 'warning', label: 'Awaiting approval' }
  }
  const { color, label } = meta[status]
  return <Mtds.Tag data-color={color}>{label}</Mtds.Tag>
}

export const CombinationsTable = ({
  data,
  showCodes,
  pagination,
  setPagination
}: CombinationsTableProps) => {
  const [reviewRows, setReviewRows] = useState<
    ContractAnalysisRow[] | undefined
  >(undefined)
  const columnHelper = createColumnHelper<
    typeof selectablePaginatedTableFeatures,
    ContractAnalysisRow
  >()
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const columns = useMemo(() => {
    const allAwaiting =
      data.length > 0 && data.every((row) => row.status === 'awaiting')

    const selectColumn = allAwaiting
      ? [
          // prettier-ignore
          columnHelper.display({
            id: 'select',
            header: ({ table }) => ( // NOSONAR
              <Mtds.Input
                type={'checkbox'}
                checked={table.getIsAllRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
                ref={(el) => {
                  if (el) el.indeterminate = table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
                }}
              />
            ),
            cell: ({ row }) => ( // NOSONAR
              <Mtds.Input
                type={'checkbox'}
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onChange={row.getToggleSelectedHandler()}
              />
            )
          })
        ]
      : []

    const baseColumns = [
      // prettier-ignore
      columnHelper.display({
        id: 'status',
        header: 'Status',
        cell: (info) => ( // NOSONAR
          <Link
            id={`combination-${info.row.original.analysisType.id}`}
            to={`/combinations/${info.row.original.analysisType.id}`}
          >
            <StatusCell status={info.row.original.status} />
          </Link>
        )
      }),
      columnHelper.accessor('analysisType.analysisType.sampleType.product', {
        header: 'Product',
        cell: (info) => {
          const sampleType =
            info.row.original.analysisType.analysisType?.sampleType
          return renderWithCode(
            info.getValue(),
            sampleType?.productCode,
            showCodes
          )
        }
      }),
      columnHelper.accessor('analysisType.analysisType.sampleType.matrix', {
        header: 'Matrix',
        cell: (info) => {
          const sampleType =
            info.row.original.analysisType.analysisType?.sampleType
          return renderWithCode(
            info.getValue(),
            sampleType?.matrixCode,
            showCodes
          )
        }
      }),
      columnHelper.accessor('analysisType.analysisType.substances', {
        enableSorting: false,
        header: 'Substances',
        cell: (info) => (
          <SubstanceTags
            analysisType={info.row.original.analysisType.analysisType}
            showAllWhenFits={false}
            showCodes={showCodes}
          />
        )
      }),
      columnHelper.accessor('analysisType.analysisType.payloadSchema', {
        enableSorting: false,
        header: 'Schema',
        cell: (info) => {
          const value = info.getValue()
          return value?.name ?? '-'
        }
      })
    ]

    const hasDeclined = data.some((row) => row.status === 'declined')
    const reasonColumn = hasDeclined
      ? [
          // prettier-ignore
          columnHelper.display({
            id: 'reason',
            header: 'Reason',
            cell: ({ row }) => { // NOSONAR
              if (row.original.status !== 'declined') return null
              const comment = row.original.analysisType?.approvalComment ?? '-'
              return <span>{comment}</span>
            }
          })
        ]
      : []

    return columnHelper.columns([
      ...selectColumn,
      ...baseColumns,
      ...reasonColumn
    ])
  }, [columnHelper, data, showCodes])

  const table = useTable({
    features: selectablePaginatedTableFeatures,
    data,
    columns,
    state: {
      sorting,
      pagination,
      rowSelection
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    manualPagination: false,
    autoResetAll: false
  })

  // Clear any row selections when the underlying data changes
  useEffect(() => {
    setRowSelection({})
  }, [data])

  return (
    <>
      <figure>
        <Mtds.Table
          data-align='center'
          aria-label='Contract analysis types table'
          data-nowrap
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Mtds.Table.ThSortable
                    id={header.id}
                    key={header.id}
                    aria-sort={header.column.getIsSorted()}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Mtds.Table.ThSortable>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                id={row.id}
                data-clickdelegatefor={`combination-${row.original.analysisType.id}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Mtds.Table>
      </figure>
      <Mtds.Flex data-align='center' data-justify='space-between'>
        <Mtds.Pagination
          aria-label='Sidenavigering'
          current={table.state.pagination.pageIndex + 1}
          total={table.getPageCount()}
          show={table.state.pagination.pageSize}
          props={({ page }) => ({
            onClick: () => {
              window.scrollTo({ top: 0 })
              table.setPageIndex(page - 1)
            }
          })}
        />
        <p>
          Showing {table.getRowModel().rows.length} of{' '}
          {table.getPrePaginatedRowModel().rows.length}
        </p>
        <Mtds.Field
          as='select'
          value={table.state.pagination.pageSize.toString() || '20'}
          onChange={({ target }) => table.setPageSize(Number(target.value))}
          options={[
            { value: '10', label: 'Show 10 per page' },
            { value: '20', label: 'Show 20 per page' },
            { value: '50', label: 'Show 50 per page' },
            { value: '100', label: 'Show 100 per page' },
            { value: '200', label: 'Show 200 per page' }
          ]}
        />
      </Mtds.Flex>
      {table.getSelectedRowModel().rows.length > 0 && (
        <SaveChangesBanner
          onSave={() => {
            setReviewRows(
              table.getSelectedRowModel().rows.map((r) => r.original)
            )
          }}
          onDiscard={() => table.resetRowSelection()}
          saveButtonLabel={`Review selected (${table.getSelectedRowModel().rows.length})`}
          discardButtonLabel={'Clear selection'}
          headerText='Review multiple combinations'
        />
      )}
      <ReviewCombinationModal
        review={reviewRows}
        onClose={() => setReviewRows(undefined)}
      />
    </>
  )
}
